import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Box,
  Typography,
  Slide,
  Portal,
} from '@mui/material';
import { Refresh, Update, Close } from '@mui/icons-material';
import { checkForUpdates, forceReload, getVersionInfo } from '../utils/version';

export const UpdateNotification: React.FC = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Check for updates on app load
    const hasUpdate = checkForUpdates();
    
    // Check for test flag
    const testUpdate = localStorage.getItem('test_update_notification') === 'true';
    
    if (hasUpdate) {
      setShowUpdate(true);
    }
    
    if (testUpdate) {
      setShowUpdate(true);
      localStorage.removeItem('test_update_notification');
    }

    // Check for updates periodically (every 5 minutes)
    const interval = setInterval(() => {
      const hasUpdate = checkForUpdates();
      if (hasUpdate) {
        setShowUpdate(true);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  const handleUpdate = () => {
    setIsUpdating(true);
    // Small delay to show loading state
    setTimeout(() => {
      forceReload();
    }, 500);
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <Portal>
      <Slide direction="down" in={showUpdate} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: { 
              xs: 'calc(env(safe-area-inset-top) + 16px)', 
              sm: 'calc(env(safe-area-inset-top) + 24px)' 
            },
            left: { xs: 16, sm: '50%' },
            right: { xs: 16, sm: 'auto' },
            transform: { xs: 'none', sm: 'translateX(-50%)' },
            zIndex: 9999, // High z-index to appear above everything
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 500 },
            width: { xs: 'auto', sm: 500 },
          }}
        >
          <Alert
            severity="info"
            sx={{
              width: '100%',
              boxShadow: 4,
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%',
                flex: 1,
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
                paddingTop: 0,
                marginRight: 0,
              },
            }}
            icon={<Update sx={{ fontSize: { xs: 20, sm: 24 } }} />}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={handleUpdate}
                disabled={isUpdating}
                startIcon={
                  isUpdating ? (
                    <Refresh 
                      sx={{ 
                        animation: 'spin 1s linear infinite',
                        fontSize: { xs: 16, sm: 18 },
                      }} 
                    />
                  ) : (
                    <Refresh sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  )
                }
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  minWidth: { xs: 100, sm: 120 },
                  height: { xs: 32, sm: 36 },
                  px: { xs: 1, sm: 1.5 },
                  borderColor: 'currentColor',
                  '&:hover': {
                    borderColor: 'currentColor',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              >
                {isUpdating ? 'Updating...' : 'Update Now'}
              </Button>
            }
          >
            <Box>
              <Typography 
                variant="body2" 
                fontWeight="medium"
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  lineHeight: 1.4,
                }}
              >
                New version available!
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  lineHeight: 1.3,
                  mt: 0.5,
                }}
              >
                Refresh to get the latest features and improvements.
              </Typography>
            </Box>
          </Alert>
        </Box>
      </Slide>
    </Portal>
  );
}; 